import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { materialService } from '../../services/materialService';
import { useReferenceCurrencies, useReferenceUnits } from '../../hooks';
import { formatCurrency, formatUnit, withPerUnit } from '../../utils/formatUnit';
import { buildCurrencyOptions, buildUnitOptions } from '../../utils/chartConversion';

/**
 * Dropdown fuer Zielwaehrung bzw. -einheit einer Reihe. value null = Original.
 * Ist nichts umrechenbar, bleibt der Button deaktiviert und zeigt den Grund.
 */
export const ConversionSelect = ({ type, choice, value, isOpen, onToggle, onSelect }) => {
  const { t } = useTranslation();
  const original = choice.options.find((o) => o.isOriginal);
  const originalLabel = original?.code
    ? t('charts.conversion.original', { code: type === 'unit' ? formatUnit(original.code) : original.code })
    : t('charts.conversion.originalNone');
  const selected = value ? choice.options.find((o) => o.value && o.value.toLowerCase() === String(value).toLowerCase()) : null;
  const originalShort = type === 'unit' ? formatUnit(original?.code) : original?.code;
  const buttonLabel = selected ? selected.short : (originalShort || '—');
  const hint = choice.hint
    ? t(`charts.conversion.hint.${choice.hint}`, type === 'unit'
      ? { unit: formatUnit(original?.code) || original?.code || '' }
      : { code: original?.code || '' })
    : null;
  const disabled = choice.disabled;
  let shownRestHeader = false;

  return (
    <>
      <button
        type="button"
        className="chart-settings-dropdown"
        data-testid={`conversion-${type}`}
        disabled={disabled}
        title={hint || undefined}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => !disabled && onToggle()}
      >
        {type === 'currency' && <span className="currency-icon">💰</span>}
        <span>{buttonLabel}</span>
        {!disabled && <span className="dropdown-arrow">▼</span>}
      </button>
      {disabled && hint && (
        <div className="chart-settings-conversion-hint">{hint}</div>
      )}
      {isOpen && !disabled && (
        <div className="chart-settings-dropdown-menu" role="listbox">
          {choice.options.map((option) => {
            const header = !shownRestHeader && option.group === 'rest'
              && choice.options.some((o) => o.group === 'top');
            if (header) shownRestHeader = true;
            const isSelected = option.isOriginal ? !selected : selected?.value === option.value;
            return (
              <React.Fragment key={option.value || '__original'}>
                {header && (
                  <div className="chart-settings-dropdown-group">{t('charts.conversion.moreCurrencies')}</div>
                )}
                <div
                  role="option"
                  aria-selected={isSelected}
                  className={`chart-settings-dropdown-item${isSelected ? ' active' : ''}`}
                  onClick={() => onSelect(option.value)}
                >
                  <span>{option.isOriginal ? originalLabel : option.label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </>
  );
};

const ChartSettings = ({
  isOpen,
  onClose,
  comparisons = [],
  materialCode,
  material,
  onApply,
  mode = 'value',
  numberFormat = 'auto',
  initialSettings = null,
}) => {
  const { i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || 'en';
  const popupRef = useRef(null);
  // Nur laden, wenn der Dialog offen ist; danach aus dem Cache.
  const { data: referenceCurrencies } = useReferenceCurrencies({ enabled: isOpen });
  const { data: referenceUnits } = useReferenceUnits({ enabled: isOpen });

  // Create settings from comparisons
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState(mode === 'percent' ? '%-Change' : 'Regular Chart');
  const [numberFormatSelection, setNumberFormatSelection] = useState(numberFormat || 'auto');

  // Update settings when comparisons change
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    // If caller provided settings, use them
    if (initialSettings && Array.isArray(initialSettings) && initialSettings.length) {
      setSettings(initialSettings);
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      setLoading(true);
      setLoadError(null);

      const buildPriceDisplay = (general) => {
        // Aus Wert + Waehrung + Einheit, last_price_display enthaelt "n/a/GWh" o. ae.
        const value = Number(general?.last_price_value);
        if (general?.last_price_value != null && Number.isFinite(value)) {
          const formatted = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          return withPerUnit(formatted, general.currency, general.value_unit);
        }
        return general?.last_price_display || '—';
      };
      // Keine Defaults 'USD'/'mt': Reihen ohne Waehrung oder Einheit bleiben ohne.
      const cleanUnit = (u) => (formatUnit(u) ? String(u).trim() : '');

      let mainGeneral = material;
      if (!mainGeneral && materialCode) {
        try {
          mainGeneral = await materialService.getGeneral(materialCode);
        } catch (err) {
          // fall back to empty
        }
      }

      const baseUnit = cleanUnit(mainGeneral?.value_unit);
      const baseCurrency = formatCurrency(mainGeneral?.currency) || formatCurrency(material?.currency);

      const mainSetting = {
        id: materialCode || 'main',
        code: materialCode || 'main',
        name: mainGeneral?.name || material?.name || 'Main Commodity',
        price: buildPriceDisplay(mainGeneral),
        baseCurrency,
        baseUnit,
        // null = Original; umgerechnet wird im Backend
        targetCurrency: null,
        targetUnit: null,
        color: '#8B5CF6',
      };

      try {
        // Fetch general info for main + comparisons
        const comparisonPromises = comparisons.map(async (comparison) => {
          try {
            const general = await materialService.getGeneral(comparison.code);
            const baseUnit = cleanUnit(general?.value_unit);
            const baseCurrency = formatCurrency(general?.currency);
            return {
              id: comparison.id,
              code: comparison.code,
              name: comparison.name,
              price: buildPriceDisplay(general),
              baseCurrency,
              baseUnit,
              targetCurrency: null,
              targetUnit: null,
              color: comparison.color,
            };
          } catch (err) {
            const baseUnit = cleanUnit(comparison?.unit);
            const baseCurrency = formatCurrency(comparison?.currency);
            return {
              id: comparison.id,
              code: comparison.code,
              name: comparison.name,
              price: '—',
              baseCurrency,
              baseUnit,
              targetCurrency: null,
              targetUnit: null,
              color: comparison.color,
            };
          }
        });

        const comparisonSettings = await Promise.all(comparisonPromises);
        if (!cancelled) {
          setSettings([mainSetting, ...comparisonSettings]);
        }
      } catch (error) {
        if (!cancelled) setLoadError(error?.message || 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSettings();
    return () => { cancelled = true; };
  }, [comparisons, initialSettings, isOpen, material, materialCode]);

  const [openDropdown, setOpenDropdown] = useState(null); // { itemId, type: 'currency' | 'unit' }
  useEffect(() => {
    setActiveTab(mode === 'percent' ? '%-Change' : 'Regular Chart');
  }, [mode]);
  useEffect(() => {
    setNumberFormatSelection(numberFormat || 'auto');
  }, [numberFormat, isOpen]);

  const handleTargetChange = (itemId, field, value) => {
    setSettings(prev => prev.map(item =>
      item.id === itemId ? { ...item, [field]: value || null } : item
    ));
    setOpenDropdown(null);
  };

  const handleColorChange = (itemId, color) => {
    setSettings(prev => prev.map(item =>
      item.id === itemId ? { ...item, color } : item
    ));
  };

  const toggleDropdown = (itemId, type) => {
    if (openDropdown?.itemId === itemId && openDropdown?.type === type) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown({ itemId, type });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="chart-settings-popup-overlay" onClick={onClose} />
      <div className="chart-settings-popup" ref={popupRef}>
        <h2 className="chart-settings-title">CHART SETTINGS</h2>

        {loading && (
          <div className="chart-settings-loading">Loading...</div>
        )}
        {loadError && (
          <div className="chart-settings-error">{loadError}</div>
        )}

        <div className="chart-settings-header">
          <span>Name</span>
          <span>Price</span>
          <span>Currency</span>
          <span>Unit</span>
        </div>

        <div className="chart-settings-items">
          {settings.map((item) => (
            <div key={item.id} className="chart-settings-item">
              <div className="chart-settings-item-name">
                <input
                  className="chart-settings-item-color"
                  type="color"
                  value={item.color || '#8B5CF6'}
                  onChange={(e) => handleColorChange(item.id, e.target.value)}
                  title="Change series color"
                  aria-label={`Change color for ${item.name}`}
                />
                <span>{item.name}</span>
              </div>
              <div className="chart-settings-item-price">{item.price}</div>
              <div className="chart-settings-item-currency" style={{ position: 'relative' }}>
                <ConversionSelect
                  type="currency"
                  choice={buildCurrencyOptions({ currencies: referenceCurrencies, sourceCurrency: item.baseCurrency, language })}
                  value={item.targetCurrency}
                  isOpen={openDropdown?.itemId === item.id && openDropdown?.type === 'currency'}
                  onToggle={() => toggleDropdown(item.id, 'currency')}
                  onSelect={(value) => handleTargetChange(item.id, 'targetCurrency', value)}
                />
              </div>
              <div className="chart-settings-item-unit" style={{ position: 'relative' }}>
                <ConversionSelect
                  type="unit"
                  choice={buildUnitOptions({ units: referenceUnits, sourceUnit: item.baseUnit, language })}
                  value={item.targetUnit}
                  isOpen={openDropdown?.itemId === item.id && openDropdown?.type === 'unit'}
                  onToggle={() => toggleDropdown(item.id, 'unit')}
                  onSelect={(value) => handleTargetChange(item.id, 'targetUnit', value)}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="chart-settings-tabs">
          <button
            className={`chart-settings-tab ${activeTab === 'Regular Chart' ? 'active' : ''}`}
            onClick={() => setActiveTab('Regular Chart')}
          >
            Regular Chart
          </button>
          <button
            className={`chart-settings-tab ${activeTab === '%-Change' ? 'active' : ''}`}
            onClick={() => setActiveTab('%-Change')}
          >
            %-Change
          </button>
        </div>

        <div style={{ marginTop: '12px', marginBottom: '8px' }}>
          <div style={{ color: '#CBD5E1', fontSize: '12px', marginBottom: '8px' }}>Number Format</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { value: 'auto', label: 'Auto' },
              { value: 'us', label: 'US (1,234.56)' },
              { value: 'eu', label: 'EU (1.234,56)' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                className={`chart-settings-tab ${numberFormatSelection === option.value ? 'active' : ''}`}
                onClick={() => setNumberFormatSelection(option.value)}
                style={{ padding: '8px 12px' }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="chart-settings-actions">
          <button className="chart-settings-btn chart-settings-btn-cancel" onClick={onClose}>
            CANCEL
          </button>
          <button
            className="chart-settings-btn chart-settings-btn-save"
            onClick={() => {
              if (onApply) {
                onApply({
                  mode: activeTab === '%-Change' ? 'percent' : 'value',
                  settings,
                  numberFormat: numberFormatSelection,
                });
              }
              onClose();
            }}
            disabled={loading}
          >
            SAVE
          </button>
        </div>
      </div>
    </>
  );
};

export default ChartSettings;
